import axios from 'axios';
import { Cacheable } from './cache';

export const API_BASE_URL = "https://photoskiff.com/zaruba/dbapi";

// Get all contests
export const getContests = Cacheable('contests_getContests')(async () => {
  const response = await axios.get(`${API_BASE_URL}/contests`);
  return response.data;
});

export const getContest = Cacheable('contests_getContest')(async (contestId: string) => {
  const response = await axios.get(`${API_BASE_URL}/contests/${contestId}`);
  return response.data;
});

export const getContestRankingOrder = Cacheable('contests_getContestRankingOrder')(async (contestId: string): Promise<'asc' | 'desc'> => {
  const contest = await getContest(contestId);
  const order = String(contest?.ranking_order ?? 'desc').toLowerCase();
  return order === 'asc' ? 'asc' : 'desc';
});

export const getContestPresentationData = Cacheable('contests_getContestPresentationData')(async (contestId: number) => {
  const response = await axios.get(`${API_BASE_URL}/contests/${contestId}/presentation`);
  return response.data;
});

export const getContestRankingData = Cacheable('contests_getContestRankingData')(async (contestId: number) => {
  const response = await axios.get(`${API_BASE_URL}/contests/${contestId}/ranking`);
  return response.data;
});

export const checkParticipantExists = Cacheable('contests_checkParticipantExists')(async (id: number) => {
  const response = await axios.get(`${API_BASE_URL}/admin/participant-exists/${id}`);
  return response.data.exists;
});

export const getParticipantById2 = Cacheable('contests_getParticipantById2')(async (id: number)  => {
  const response = await axios.get(`${API_BASE_URL}/participants2/${id}`);
  const data = response.data;
  const info = data.info ? JSON.parse(data.info) : {};
  return {...data, info:info.info, links:info.links};
});

export type ParticipantRank = {
  rank_id: number;
  rank_type: string;
  contest_id: number;
  contest_name: string;
  result_group: string | null;
  result: number | null;
};

export type ParticipantProfile = {
  participant_id: number;
  name: string;
  age?: number;
  gender?: string;
  age_group_id?: number;
  weight?: number;
  profile_picture?: string;
  external_id?: string | null;
  created_at?: string;
  info?: string;
  links?: { link: string; description?: string }[];
  ranks?: ParticipantRank[];
};

export const getParticipantById = Cacheable('contests_getParticipantById')(async (id: string): Promise<ParticipantProfile>  => {
  const response = await axios.get(`${API_BASE_URL}/participants/${id}`);
  const data = response.data;
  const info = data.info ? JSON.parse(data.info) : {};
  const ranks: ParticipantRank[] = Array.isArray(data.ranks) ? data.ranks : [];
  return {...data, info:info.info, links:info.links, ranks};
});

export interface Participant {
  participant_id: number;
  name: string;
  age: number;
  created_at: string;
  external_id: string | null;
  gender: string;
  age_group_name: string;
  total_entries: number;
  distinct_contests: number;
  profile_picture: string;
}

export interface AgeGroupShort {
  age_group_id: number;
  age_group_name: string;
}

// Function to fetch participants
export const fetchParticipants = Cacheable('contests_fetchParticipants')(async (): Promise<Participant[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/participants`);
    return response.data;
  } catch (error) {
    console.error('Error fetching participants:', error);
    throw error;
  }
});

// Upload profile picture
export const uploadProfilePicture = async (profilePicture: File): Promise<string> => {
  const formData = new FormData();
  formData.append('profile_picture', profilePicture);

  const response = await axios.post(`${API_BASE_URL}/upload-profile-picture`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data.tempImageName;
};

// Add or update a participant
export const addOrUpdateParticipant = async (payload: any) => {
  await axios.post(`${API_BASE_URL}/participants`, payload);
};

// Fetch participant by ID
export const fetchParticipantById = Cacheable('contests_fetchParticipantById')(async (participantId: number): Promise<Participant> => {
  const response = await axios.get(`${API_BASE_URL}/participants/${participantId}`);
  return response.data;
});

export interface RankingEntry {
  participant_name: string;
  participant_id: number;
  ranking: number;
  result: number;
  result_unit: string;
  grouping: string;
  evidence_url: string;
  profile_picture: string;
  created_at?: string;
  [key: string]: any;
}

export interface ContestField {
  field_name: string;
  display_label: string;
  result_field: boolean;
  non_entry_field?: boolean;
  main_result_field?: boolean;
}

export type ContestGrouping = {
  grouping: string,
  group_count: number
}

export const fetchContestGroupings = Cacheable('contests_fetchContestGroupings')(async (contestId: string): Promise<string[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/contest/${contestId}/groupings`);
    return response.data;
  } catch (error) {
    console.error('Error fetching groupings:', error);
    throw error;
  }
});

export const fetchContestGroupings2 = Cacheable('contests_fetchContestGroupings2')(async (contestId: string): Promise<ContestGrouping[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/contest/${contestId}/groupings2`);
    return response.data;
  } catch (error) {
    console.error('Error fetching groupings:', error);
    throw error;
  }
});

export const fetchContestRankings = Cacheable('contests_fetchContestRankings')(async (contestId: string, grouping: string): Promise<RankingEntry[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/contests/${contestId}/rankings`, {
      params: { grouping },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching rankings:', error);
    throw error;
  }
});

export const fetchContestMetadata = Cacheable('contests_fetchContestMetadata')(async (contestId: string): Promise<ContestField[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/contest/${contestId}/metadata`);
    const resultFields = response.data?.fields?.filter((field: ContestField) => field.result_field) ?? [];
    return resultFields;
  } catch (error) {
    console.error('Error fetching contest metadata:', error);
    throw error;
  }
});

export interface AgeGroup {
  age_group_id: number;
  age_group_name: string;
  min_age: number;
  max_age: number;
}

// Add this function in api/contests.ts
export const fetchAgeGroups = Cacheable('contests_fetchAgeGroups')(async (): Promise<AgeGroup[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/age-groups`);
    return response.data;
  } catch (error) {
    console.error('Error fetching age groups:', error);
    throw error;
  }
});

export interface Entry {
  participant_id: number;
  name: string,
  username: string;
  first_name: string;
  last_name?: string;
  contest_id: number;
  contest_name: string;
  profile_picture: string;
  participant_data?: string;
  evidence_url?: string;
  timestamp?: string;
  grouping: string;
}

// API call to fetch the latest contest entries
export const fetchLatestEntries = Cacheable('contests_fetchLatestEntries')(async (limit: number = 10): Promise<Entry[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/latest-entries`, {
      params: { limit }
    });
    return response.data as Entry[];
  } catch (error) {
    console.error('Error fetching latest entries:', error);
    throw error;
  }
});

export const fetchPodiumEntries = Cacheable('contests_fetchPodiumEntries')(
    async (topEntryCount: number, days?: string): Promise<PodiumEntry[]> => {
      const query = days ? `&days=${days}` : '';
      const response = await axios.get(
          `${API_BASE_URL}/rankings?top_entries=${topEntryCount}${query}`
      );
      return response.data;
    }
);

// Don't forget to export PodiumEntry type if needed
export interface PodiumEntry {
  contest_id: number;
  participant_id: number;
  contest_name: string;
  participant_name: string;
  profile_picture: string;
  result: string | number;
  result_unit: string;
  grouping: string;
  ranking: number;
  total_participants: number;
  created_at: string;
}

// Rank types are managed in DB and can evolve (e.g., 1РЗ/2РЗ/3РЗ or legacy 1Р).
// Keep this type open-ended so UI doesn't crash on new values.
export type RankType = string;

export type Rank = {
  rank_id: number;
  rank_type: RankType;
  contest_id: number;
  contest_name?: string;
  result_group: string | null;
  result: number | null;
};

export type ParticipantRankRow = {
   participant_id: number;
   participant_name?: string;
   rank_id: number;
   rank_type: RankType;
   contest_id: number;
   contest_name: string;
   result_group: string | null;
   result: number | null;
};

export const fetchAllRanks = Cacheable('contests_fetchAllRanks')(async (): Promise<Rank[]> => {
  const response = await axios.get(`${API_BASE_URL}/ranks`);
  return response.data;
});

export const fetchAllParticipantRanks = Cacheable('contests_fetchAllParticipantRanks')(async (): Promise<ParticipantRankRow[]> => {
  const response = await axios.get(`${API_BASE_URL}/participant-ranks`);
  return response.data;
});
